import React from "react";
import { CellState, StagePosition } from "../../config/offeringCatalog";
import {
  ALC_DOT,
  CURRENT_COLUMN_TINT,
  stageMarkerClasses,
} from "../../config/accentStyles";

interface MatrixCellProps {
  kind: "stage" | "alacarte";
  state?: CellState;
  alaCarte?: boolean;
  highlighted?: boolean;
  stagePosition?: StagePosition;
  connectLeft?: boolean;
  connectRight?: boolean;
}

const cellClasses = (highlighted?: boolean) =>
  [
    "relative flex min-h-[56px] items-center justify-center px-3 py-3",
    "border-b border-l border-gray-100 dark:border-slate-800 text-sm",
    highlighted ? CURRENT_COLUMN_TINT : "",
  ]
    .filter(Boolean)
    .join(" ");

const Dash = () => (
  <span aria-label="Not included" className="text-gray-300 dark:text-slate-600">
    &ndash;
  </span>
);

const Connector: React.FC<{ side: "left" | "right" }> = ({ side }) => (
  <span
    aria-hidden="true"
    className={[
      "pointer-events-none absolute top-1/2 h-px -translate-y-1/2 bg-gray-200 dark:bg-slate-600",
      side === "left" ? "left-0 right-1/2" : "left-1/2 right-0",
    ].join(" ")}
  />
);

const MatrixCell: React.FC<MatrixCellProps> = ({
  kind,
  state,
  alaCarte,
  highlighted,
  stagePosition,
  connectLeft,
  connectRight,
}) => {
  let marker: React.ReactNode = <Dash />;

  if (kind === "alacarte") {
    marker = alaCarte ? (
      <span
        aria-label="Available a la carte"
        className={`relative h-3 w-3 rounded-full ${ALC_DOT}`}
      />
    ) : (
      <Dash />
    );
  } else if (state === "new" && stagePosition) {
    marker = (
      <span
        aria-label="New at this stage"
        className={`relative inline-flex h-[26px] w-[26px] items-center justify-center rounded-full border text-sm font-bold ${stageMarkerClasses[stagePosition]}`}
      >
        +
      </span>
    );
  } else if (state === "carried") {
    marker = (
      <span
        aria-label="Carried forward"
        className="relative h-2 w-2 rounded-full bg-gray-300 dark:bg-slate-600"
      />
    );
  }

  return (
    <div className={cellClasses(highlighted)}>
      {kind === "stage" && connectLeft && <Connector side="left" />}
      {kind === "stage" && connectRight && <Connector side="right" />}
      {marker}
    </div>
  );
};

export default MatrixCell;
